/**
 * streakData — arma el calendario de la racha desde `/progress/daily`.
 *
 * FUENTE ÚNICA: el mismo endpoint server-side y tz-aware que alimenta al
 * Camino. Antes esto agrupaba `listSessions` client-side y sumaba
 * `durationSeconds` (wall-clock `ended_at - started_at`), que se dispara a
 * horas en sesiones dejadas abiertas: la Home podía decir "45 min hoy" y la
 * estación Hoy del Camino "12 min" con los mismos datos. El backend agrega
 * `total_audio_seconds` (audio realmente intercambiado), y ésa es ahora la
 * única definición de "minutos practicados" en toda la app.
 *
 * La racha actual / mejor marca tampoco se calculan acá: vienen de
 * `/progress/totals` vía `useProgressTotals`. El costo en USD se quitó a
 * propósito de la superficie de motivación — vive en wallet / SessionCostSheet.
 *
 * El DESGLOSE de un día (qué actividades hiciste) sí necesita las sesiones de
 * ese día, que la hoja carga lazy al abrir el detalle
 * ({@link buildDayBreakdown}). Sus minutos salen de `activeSeconds`, no del
 * wall-clock, para que el desglose cierre contra el total del mismo día.
 */
import type { DailyProgress } from '../../../lib/englishApi.js';
import type { SessionActivityType, SessionHistoryItem } from '../../../lib/sessionsApi.js';
import { ACTIVITY_TYPES } from '../../activity/activityFormat.js';

export interface StreakDayItem {
  type: SessionActivityType;
  label: string;
  minutes: number;
}

export interface StreakCat {
  type: SessionActivityType;
  label: string;
  color: string;
  minutes: number;
}

/** Resumen de un día del calendario. Todo del backend, nada derivado acá. */
export interface StreakDay {
  minutes: number;
  vocabUsed: number;
  corrections: number;
}

export interface StreakCalendar {
  /** Mapa día → resumen. Clave `${y}-${m0}-${d}` (m0 = mes 0-indexado). */
  byKey: Record<string, StreakDay>;
  /**
   * Cota inferior del navegador de meses: el más RECIENTE entre el primer día
   * activo y el inicio de la ventana pedida. Dejar navegar más atrás pintaría
   * meses vacíos que sí tuvieron actividad (el defecto silencioso que este
   * módulo vino a matar).
   */
  startDate: Date | null;
  /**
   * Zona horaria en la que el backend etiquetó estos días. Viaja con el
   * calendario (y no se re-deriva del browser) para que el detalle de un día
   * se pida en el MISMO huso que lo agrupó. `null` = sin datos.
   */
  tz: string | null;
}

/** Clave de día local consistente entre el builder y el calendario (m 0-indexado). */
export function dayKeyOf(year: number, month0: number, day: number): string {
  return `${year}-${month0}-${day}`;
}

/** Instante ISO → etiqueta `YYYY-MM-DD` en `tz`. `null` si la tz es inválida. */
function dayLabelInTz(iso: string, tz: string): string | null {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(t);
  } catch {
    return null;
  }
}

/** `YYYY-MM-DD` → `Date` local a medianoche (sin pasar por el parser UTC). */
function dateFromLabel(label: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function buildStreakCalendar(daily: DailyProgress | null): StreakCalendar {
  const byKey: Record<string, StreakDay> = {};
  if (!daily) return { byKey, startDate: null, tz: null };

  for (const d of daily.days) {
    const dt = dateFromLabel(d.date);
    if (!dt) continue;
    byKey[dayKeyOf(dt.getFullYear(), dt.getMonth(), dt.getDate())] = {
      minutes: d.minutes,
      vocabUsed: d.newWords,
      corrections: d.corr?.total ?? 0,
    };
  }

  // `from` es un instante; los días son etiquetas locales. Se traduce en la
  // MISMA tz que usó el backend (no la del runtime) para que las dos puntas de
  // la comparación hablen del mismo calendario. Después alcanza con comparar
  // como strings: en YYYY-MM-DD el orden lexicográfico es el cronológico.
  const windowStart = dayLabelInTz(daily.from, daily.tz);
  const first = daily.firstActiveDate;
  const startLabel =
    windowStart === null || first === null ? null : first > windowStart ? first : windowStart;

  return {
    byKey,
    startDate: startLabel ? dateFromLabel(startLabel) : null,
    tz: daily.tz,
  };
}

/** Nivel de intensidad de una celda (heatmap) según minutos del día. */
export function streakTier(minutes: number): number {
  if (minutes <= 0) return 0;
  if (minutes < 12) return 1;
  if (minutes < 25) return 2;
  if (minutes < 40) return 3;
  if (minutes < 60) return 4;
  return 5;
}

function dayRow(daily: DailyProgress | null, todayLabel: string) {
  return daily?.days.find((d) => d.date === todayLabel) ?? null;
}

/** Minutos practicados hoy (stat "Hoy" / sub-header del Home). */
export function minutesToday(daily: DailyProgress | null, todayLabel: string): number {
  return dayRow(daily, todayLabel)?.minutes ?? 0;
}

/**
 * ¿Hoy ya cuenta para la racha? El criterio es el del backend — hubo una
 * sesión real o un repaso hecho —, NO "hay minutos". Una sesión sin `ended_at` aporta 0 segundos
 * de audio pero mantiene la racha viva: derivarlo de los minutos hacía que el
 * hero dijera "5 días seguidos" y la hoja "te falta hoy" al mismo tiempo.
 */
export function todayCounts(daily: DailyProgress | null, todayLabel: string): boolean {
  if ((dayRow(daily, todayLabel)?.sessions ?? 0) > 0) return true;
  // Un repaso hecho también cuenta (card #604): la racha del server lo suma.
  return daily?.reviewDays.includes(todayLabel) ?? false;
}

export interface DayBreakdown {
  items: StreakDayItem[];
  cats: StreakCat[];
}

/**
 * Desglose de un día a partir de SUS sesiones (cargadas lazy al abrir el
 * detalle). Los minutos salen de `activeSeconds` — la misma señal que el total
 * del día que muestra la hoja — y no del wall-clock del historial.
 */
export function buildDayBreakdown(
  sessions: ReadonlyArray<SessionHistoryItem>,
): DayBreakdown {
  const items: StreakDayItem[] = [];
  const secsByType = new Map<SessionActivityType, number>();

  for (const s of sessions) {
    const secs = s.activeSeconds ?? 0;
    items.push({ type: s.type, label: s.title, minutes: Math.round(secs / 60) });
    secsByType.set(s.type, (secsByType.get(s.type) ?? 0) + secs);
  }

  // La barra por categoría es una PARTICIÓN del tiempo del día, así que sus
  // minutos tienen que sumar el total — redondear cada categoría por su cuenta
  // no lo garantiza (tres actividades de 90 s dan "2′+2′+2′ = 6" sobre un total
  // de 5). Se reparte el total redondeado por resto mayor.
  const entries = [...secsByType.entries()];
  const minutesByType = distributeMinutes(entries.map(([, secs]) => secs));
  const cats: StreakCat[] = entries
    .map(([type], i) => ({
      type,
      label: ACTIVITY_TYPES[type].label,
      color: ACTIVITY_TYPES[type].color,
      minutes: minutesByType[i] ?? 0,
    }))
    .sort((a, b) => b.minutes - a.minutes);

  items.sort((a, b) => b.minutes - a.minutes);
  return { items, cats };
}

/**
 * Reparte `round(Σsecs / 60)` minutos entre las partes, proporcional a sus
 * segundos, por **resto mayor**: la suma de lo repartido es exactamente el
 * total redondeado. El total se calcula sobre los segundos crudos (una sola
 * operación de redondeo) para no arrastrar el error de cada parte.
 */
function distributeMinutes(secs: readonly number[]): number[] {
  const totalSecs = secs.reduce((a, s) => a + s, 0);
  const target = Math.round(totalSecs / 60);
  if (totalSecs <= 0 || target <= 0) return secs.map(() => 0);

  const exact = secs.map((s) => (s / totalSecs) * target);
  const out = exact.map((v) => Math.floor(v));
  let rest = target - out.reduce((a, v) => a + v, 0);

  // Índices por resto decreciente; el empate lo rompe el orden original (que
  // ya viene por primera aparición) para que el reparto sea determinista.
  const byRemainder = exact
    .map((v, i) => ({ i, r: v - Math.floor(v) }))
    .sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of byRemainder) {
    if (rest <= 0) break;
    out[i] = (out[i] ?? 0) + 1;
    rest -= 1;
  }
  return out;
}
